import { ButtonIcon, IconPlus, Text } from '@spotify-internal/encore-web';
import { ListRow } from '@spotify-internal/encore-web/next';
import { DateUtils, ResourceNameLink } from '@spotify/confidence-core-react';

import {
  EXPERIMENT_TYPES,
  type SearchResultItem,
} from '../../hooks/useResourceSearch';
import { ResourceStatusLabel } from './ResourceStatusLabel';
import { RESOURCE_TYPE_CONFIG_MAP } from './resourceTypeConfig';

import styles from './SearchResourcesDialog.module.css';

const Separator = () => <span className={styles.metaSeparator}> · </span>;

const TypeLabel = ({ item }: { item: SearchResultItem }) => {
  const config = RESOURCE_TYPE_CONFIG_MAP[item.resourceType];
  return (
    <span className={styles.typeLabel}>
      <config.icon size={12} />
      {config.singularLabel}
    </span>
  );
};

const DateLabel = ({ time }: { time: string | undefined }) => {
  if (!time) return null;
  return <>{DateUtils.xAgoShort(new Date(time))}</>;
};

interface ResourceListItemProps {
  item: SearchResultItem;
  onAdd: () => void;
}

const MetricSecondary = ({ item }: { item: SearchResultItem }) => (
  <span className={styles.secondaryRow}>
    <TypeLabel item={item} />
    {item.createTime && (
      <>
        <Separator />
        <DateLabel time={item.createTime} />
      </>
    )}
  </span>
);

const PulseSecondary = ({ item }: { item: SearchResultItem }) => (
  <span className={styles.secondaryRow}>
    <TypeLabel item={item} />
    {item.ownerName && (
      <>
        <Separator />
        {item.ownerName}
      </>
    )}
  </span>
);

const FlagSecondary = ({ item }: { item: SearchResultItem }) => (
  <span className={styles.secondaryRow}>
    <TypeLabel item={item} />
    {item.createTime && (
      <>
        <Separator />
        <DateLabel time={item.createTime} />
      </>
    )}
    {item.state && (
      <>
        <Separator />
        <ResourceStatusLabel item={item} />
      </>
    )}
  </span>
);

const ExperimentSecondary = ({ item }: { item: SearchResultItem }) => (
  <span className={styles.secondaryRow}>
    <TypeLabel item={item} />
    {item.updateTime && (
      <>
        <Separator />
        <DateLabel time={item.updateTime} />
      </>
    )}
    {item.workflowState && (
      <>
        <Separator />
        <ResourceStatusLabel item={item} />
      </>
    )}
  </span>
);

const getSecondaryComponent = (
  item: SearchResultItem,
): typeof MetricSecondary => {
  if (item.resourceType === 'flag') return FlagSecondary;
  if (item.resourceType === 'pulse') return PulseSecondary;
  if (EXPERIMENT_TYPES.includes(item.resourceType)) return ExperimentSecondary;
  return MetricSecondary;
};

export const ResourceListItem = ({ item, onAdd }: ResourceListItemProps) => {
  const Secondary = getSecondaryComponent(item);

  return (
    <ListRow size="large" className={styles.listItem}>
      <div className={styles.listItemTextGroup}>
        <Text variant="bodySmall" lineClamp={1}>
          <ResourceNameLink name={item.name} text={item.displayName}  data-track-source-anchor="4d03b98fd9;resource-list-item-resource-name-link"/>
        </Text>
        <Secondary item={item} />
      </div>
      <ButtonIcon
        variant="tertiary"
        aria-label={`Add ${item.displayName} to chat`}
        onClick={onAdd}
       data-track-source-anchor="c679bdbf6e;resource-list-item-button-icon;low">
        <IconPlus />
      </ButtonIcon>
    </ListRow>
  );
};
